import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useLogout } from '@/hooks/useLogout';
import {
  assignmentsApi, doctorComparisonApi, getErrorMessage, getMediaUrl,
  markNotificationsForOpenContext,
  type Assignment, type ComparisonOverview, type Patch,
} from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { NotificationBell } from '@/components/NotificationBell';
import { StatusBadge } from '@/components/StatusBadge';
import { ComparisonReconciliationCard } from '@/components/ComparisonReconciliationCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Loader2, Microscope } from 'lucide-react';
import { toast } from 'sonner';
import {
  allPatchesVisited,
  ASSIGNMENT_REVIEW_SAVED_EVENT,
  clearSession,
  isPatchVisited,
} from '@/lib/assignmentReviewSession';

function patchReviewBadge(patch: Patch) {
  if (patch.review_status === 'saved') {
    return patch.is_modified
      ? { label: 'Saved (modified)', className: 'bg-amber-100 text-amber-800' }
      : { label: 'Saved', className: 'bg-green-100 text-green-800' };
  }
  return { label: 'Not reviewed', className: 'bg-gray-100 text-gray-700' };
}

export function AssignmentDetail() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleLogout = useLogout();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [patches, setPatches] = useState<Patch[]>([]);
  const [comparison, setComparison] = useState<ComparisonOverview | null>(null);
  const [doctorNote, setDoctorNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!assignmentId) return;
    setLoading(true);
    try {
      const [{ data: assign }, { data: patchList }] = await Promise.all([
        assignmentsApi.detail(assignmentId),
        assignmentsApi.patches(assignmentId),
      ]);
      setAssignment(assign);
      setDoctorNote(assign.doctor_note || '');
      setPatches(Array.isArray(patchList) ? patchList : []);

      if (assign.status === 'submitted') {
        try {
          const { data: comp } = await doctorComparisonApi.overview(assignmentId);
          setComparison(comp);
        } catch {
          setComparison(null);
        }
      } else {
        setComparison(null);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
      navigate('/doctor');
    } finally {
      setLoading(false);
    }
  }, [assignmentId, navigate]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!assignmentId) return;
    void markNotificationsForOpenContext({ assignment_id: assignmentId });
  }, [assignmentId]);

  useEffect(() => {
    if (!assignmentId) return;
    const onSaved = (event: Event) => {
      const detail = (event as CustomEvent<{ assignmentId: string }>).detail;
      if (detail?.assignmentId === assignmentId) {
        void load();
      }
    };
    window.addEventListener(ASSIGNMENT_REVIEW_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(ASSIGNMENT_REVIEW_SAVED_EVENT, onSaved);
  }, [assignmentId, load]);

  useEffect(() => {
    if (assignment?.status === 'submitted' && assignmentId) {
      clearSession(assignmentId);
    }
  }, [assignment?.status, assignmentId]);

  // const patchIds = patches.map((p) => p.id);
  // const allVisited = assignmentId ? allPatchesVisited(assignmentId, patchIds) : false;
  // const allSaved = patches.length > 0 && patches.every((p) => p.review_status === 'saved');
  // const canSubmit = allVisited && allSaved;

  const patchIds = patches.map((p) => p.id);
  const allSaved = patches.length > 0 && patches.every((p) => p.review_status === 'saved');
/** Opened this session OR already saved on server (survives logout). */
  const isPatchDone = (patch: Patch) =>
  patch.review_status === 'saved' || Boolean(assignmentId && isPatchVisited(assignmentId, patch.id));
  const allVisited = patches.length > 0 && patches.every(isPatchDone);
  const canSubmit = allSaved;

  const handleNoteBlur = async () => {
    if (!assignmentId) return;
    try {
      await assignmentsApi.note(assignmentId, doctorNote);
    } catch {
      /* silent */
    }
  };

  const handleSubmit = async () => {
    if (!assignmentId) return;
    if (!allSaved) {
      toast.warning('Open and save every patch before submitting your review.');
      return;
    }
    setSubmitting(true);
    try {
      await assignmentsApi.submit(assignmentId);
      clearSession(assignmentId);
      toast.success('Review submitted');
      void load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !assignment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PageHeader
        title={assignment.sample_name}
        subtitle={`${assignment.total_patches} patches — assignment overview`}
        icon={<Microscope className="size-6 text-green-600" />}
        iconBgClass="bg-green-100"
        backTo="/doctor"
        actions={
          <div className="flex items-center gap-2">
            <NotificationBell />
            <StatusBadge status={assignment.status} />
            {assignment.status !== 'submitted' && (
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                title={
                  !allSaved
                    ? 'Open and save every patch before submitting'
                    : undefined
                }
              >
                {submitting ? <Loader2 className="size-4 animate-spin mr-1" /> : <CheckCircle className="size-4 mr-1" />}
                Submit review
              </Button>
            )}
          </div>
        }
        userName={user?.name}
        userRole={user?.role ?? undefined}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6 w-full">
        {assignment.admin_note && (
          <Card>
            <CardHeader><CardTitle className="text-base">Admin note</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-gray-700">{assignment.admin_note}</p></CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Your note</CardTitle></CardHeader>
          <CardContent>
            <Label className="sr-only">Doctor note</Label>
            <Textarea
              value={doctorNote}
              onChange={(e) => setDoctorNote(e.target.value)}
              onBlur={handleNoteBlur}
              placeholder="Overall review notes…"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Patches</CardTitle>
            {assignment.status !== 'submitted' && (
              <p className="text-sm text-gray-500 font-normal">
                Open every patch — changes save automatically. Submit is enabled once all patches
                have been opened.
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {patches.map((patch) => {
                const badge = patchReviewBadge(patch);
                const visited = isPatchDone(patch);
                return (
                  <button
                    key={patch.id}
                    type="button"
                    onClick={() => navigate(`/doctor/assignments/${assignmentId}/patches/${patch.id}`)}
                    className="rounded-lg border overflow-hidden text-left hover:shadow-md focus:ring-2 focus:ring-green-500"
                  >
                    <img
                      src={getMediaUrl(patch.image_path)}
                      alt={patch.filename}
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                    <div className="p-2 space-y-1">
                      <p className="text-xs font-medium truncate">{patch.patch_identifier}</p>
                      <div className="flex flex-wrap gap-1">
                        {visited && (
                          <Badge variant="outline" className="text-[10px]">
                            Opened
                          </Badge>
                        )}
                        <Badge variant="secondary" className={`text-[10px] ${badge.className}`}>
                          {badge.label}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {assignment.status === 'submitted' && comparison && (
          <ComparisonReconciliationCard
            mode="doctor"
            assignmentId={assignmentId}
            comparison={comparison}
            onReload={load}
          />
        )}
      </main>
    </div>
  );
}
